// ── «На сегодня»: план повторения, «новых в день», серия, панель колод ─────────
// План = к повторению (зелёный) + новые (синий) до «Новых в день». «Сделано сегодня» —
// уникальные карточки. SrsPanel — колоды на сегодня с порядком и прогнозом.

import { useEffect, useMemo, useRef, useState } from "react";
import { t } from "../i18n";
import Icon from "../Icon";
import { markSetting } from "../settings";
import { dayProgress, getDeckOrder, setDeckOrder, sortByDeckOrder } from "../srs";
import { SANS } from "../themes";
import { calcStats, getStudyDays, getTotalTime, localDateKey, nCards } from "../utils";
import { Heatmap } from "./Heatmap";
import { usePalette } from "../ui/theme";
import { Button as UiButton } from "../ui";
import { panelSurface } from '../ui/surface';

// «План на сегодня» = повторить (уже пройденные, которым пора) + новые (до «Новых в
// день»). Отдельной «дневной цели» больше нет: её число спорило с планом («25 / 10»,
// а на сегодня ещё 32). Человек выбирает только, сколько новых карточек брать в день.
export const setNewLimit=(n)=>{
  const v=Math.max(0,Math.min(200,Math.round(Number(n)||0)));
  try{localStorage.setItem("ac_new_limit",String(v));markSetting("ac_new_limit");}catch{}
  window.dispatchEvent(new CustomEvent("ac:settings-updated",{detail:{keys:["ac_new_limit"]}}));
  return v;
};
// Один раз: прежняя «дневная цель» становится «Новых в день» (так её и понимали).
(()=>{try{
  if(localStorage.getItem("ac_plan_v2"))return;
  localStorage.setItem("ac_plan_v2","1");
  const lang=localStorage.getItem("ac_active_lang")||"";
  const g=parseInt(localStorage.getItem(lang?`ac_daily_goal_${lang}`:"ac_daily_goal")||localStorage.getItem("ac_daily_goal"));
  if(g>0)setNewLimit(Math.min(g,50));
}catch{}})();
// Сводка дня для экранов: сколько повторить, сколько новых, сколько уже сделано.
export function useTodayPlan(cards){
  const [tick,setTick]=useState(0);
  useEffect(()=>{
    const h=()=>setTick(x=>x+1);
    window.addEventListener("ac:settings-updated",h);window.addEventListener("ac:activity-changed",h);window.addEventListener("focus",h);
    return()=>{window.removeEventListener("ac:settings-updated",h);window.removeEventListener("ac:activity-changed",h);window.removeEventListener("focus",h);};
  },[]);
  return useMemo(()=>dayProgress(cards),[cards,tick]);// eslint-disable-line react-hooks/exhaustive-deps
}
export const planMins=(n)=>Math.max(1,Math.round(n*8/60));
// Кнопки «− 10 +» для «Новых в день»
export function NewPerDay({C,value,small=false}){
  const b={width:small?32:36,height:small?32:36,borderRadius:8,border:`1px solid ${C.border}`,background:"none",color:C.mid,cursor:"pointer",fontFamily:SANS,fontSize:16,display:"inline-flex",alignItems:"center",justifyContent:"center",padding:0};
  return(
    <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
      <button onClick={()=>setNewLimit(value-5)} aria-label={t("Меньше новых в день")} className="ac-ibtn" style={b}>−</button>
      <span style={{minWidth:30,textAlign:"center",fontWeight:600,fontVariantNumeric:"tabular-nums",color:C.text}}>{value}</span>
      <button onClick={()=>setNewLimit(value+5)} aria-label={t("Больше новых в день")} className="ac-ibtn" style={b}>+</button>
    </span>
  );
}

// «План на сегодня» в разделе «Повторение»
export function TodayPlanCard({theme,onQuiz,lang,cards}){
  const C=usePalette();
  const plan=useTodayPlan(cards);
  const pct=plan.all?Math.round(plan.done/plan.all*100):0;
  const finished=plan.total===0&&plan.done>0;
  const {listenMin,readMin}=getTotalTime(lang);
  const fmtMin=(m)=>m>=60?t("{0}ч {1}м",[Math.floor(m/60),m%60]):t("{0}м",[m]);
  const num=(n,label,hint,color)=>(
    <div style={{flex:1,minWidth:0,background:C.surface,borderRadius:10,padding:"10px 12px"}}>
      <div style={{fontSize:13,color:C.mid}}>{label}</div>
      <div style={{fontSize:24,fontWeight:700,color,fontVariantNumeric:"tabular-nums",lineHeight:1.2}}>{n}</div>
      <div style={{fontSize:12.5,color:C.faint,marginTop:2}}>{hint}</div>
    </div>
  );
  return(
    <div data-panel="" style={{...panelSurface(C),borderRadius:12,padding:"14px 16px"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:10}}>
        <span style={{fontSize:15,fontWeight:600,color:C.text}}>{t("План на сегодня")}</span>
        <span style={{fontSize:13,fontWeight:600,color:finished?C.green:C.mid}}>{finished?t("Выполнен"):plan.total?t("≈ {0} мин",[planMins(plan.total)]):""}</span>
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        {num(plan.repeat,t("К повторению"),t("уже пройденные"),C.green)}
        {num(plan.fresh,t("Новые"),plan.waitingNew>0?t("ещё {0} ждут",[plan.waitingNew]):t("из нового"),C.blueDark)}
      </div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginTop:10,flexWrap:"wrap"}}>
        <span style={{fontSize:13,color:C.mid}}>{t("Новых карточек в день")}</span>
        <NewPerDay C={C} value={plan.newLimit} small/>
      </div>
      <div role="progressbar" aria-label={t("План на сегодня")} aria-valuemin={0} aria-valuemax={plan.all} aria-valuenow={plan.done}
        style={{height:6,borderRadius:3,background:C.surface,marginTop:10,overflow:"hidden"}}>
        <div style={{height:"100%",width:`${finished?100:pct}%`,borderRadius:3,background:finished?C.green:C.blue,transition:"width .4s ease"}}/>
      </div>
      <div style={{display:"flex",gap:14,flexWrap:"wrap",fontSize:13,color:C.mid,marginTop:8}}>
        <span>{t("Сделано сегодня: {0}",[plan.done])}</span>
        {listenMin>0&&<span style={{display:"inline-flex",alignItems:"center",gap:6}}><Icon n="headphones" s={14}/>{fmtMin(listenMin)} {t("слушали")}</span>}
        {readMin>0&&<span style={{display:"inline-flex",alignItems:"center",gap:6}}><Icon n="book" s={14}/>{fmtMin(readMin)} {t("читали")}</span>}
      </div>
      {plan.total>0?(
        <UiButton variant="primary" icon="play" onClick={()=>onQuiz(null)} style={{width:"100%",marginTop:12}}>{t("Начать")}</UiButton>
      ):(
        <div style={{marginTop:12,fontSize:14,color:C.green,display:"flex",alignItems:"center",gap:6}}><Icon n="check" s={15}/>{t("На сегодня всё — возвращайтесь завтра")}</div>
      )}
    </div>
  );
}

export function SrsPanel({deckData,totalDue,onQuiz,theme,forecast,lang='',allDecks=[]}){
  const C=usePalette();
  // Порядок колод: новые карточки берутся в этом порядке. Перетаскивание за «ручку»
  // работает и мышью, и пальцем (pointer events), стрелки — запасной вариант.
  const [ordering,setOrdering]=useState(false);
  const [orderList,setOrderList]=useState([]);
  const [draggingId,setDraggingId]=useState(null);
  const rowRefs=useRef({});
  const dragId=useRef(null);
  const startOrdering=()=>{setOrderList(sortByDeckOrder(allDecks));setOrdering(true);};
  const saveOrder=(list)=>{
    const ids=list.map(p=>p.id);const mine=new Set(ids);
    // Колоды других языков (скрытые фильтром) сохраняют свой порядок — после этих.
    setDeckOrder([...ids,...getDeckOrder().filter(id=>!mine.has(id))]);
    try{markSetting('ac_deck_order');}catch{}
  };
  const moveTo=(list,id,to)=>{
    const item=list.find(p=>p.id===id);const rest=list.filter(p=>p.id!==id);
    rest.splice(Math.max(0,Math.min(rest.length,to)),0,item);return rest;
  };
  const onGripDown=(e,id)=>{e.preventDefault();dragId.current=id;setDraggingId(id);try{e.currentTarget.setPointerCapture(e.pointerId);}catch{}};
  const onGripMove=(e)=>{
    if(!dragId.current)return;
    const y=e.clientY;
    setOrderList(list=>{
      const to=list.filter(p=>p.id!==dragId.current).filter(p=>{const el=rowRefs.current[p.id];if(!el)return false;const r=el.getBoundingClientRect();return r.top+r.height/2<y;}).length;
      const from=list.findIndex(p=>p.id===dragId.current);
      return to===from?list:moveTo(list,dragId.current,to);
    });
  };
  const onGripUp=()=>{if(!dragId.current)return;dragId.current=null;setDraggingId(null);setOrderList(list=>{saveOrder(list);return list;});};
  const nudge=(id,d)=>setOrderList(list=>{const i=list.findIndex(p=>p.id===id);const next=moveTo(list,id,i+d);saveOrder(next);return next;});
  const go=(id)=>onQuiz(id);
  const maxCards=Math.max(1,...deckData.map(d=>d.allCards));

  const inner=(
    <div data-panel="" style={{...panelSurface(C),borderRadius:12,overflow:"hidden"}}>
      {/* Шапка: название, «Порядок», повторить выбранные / все */}
      <div style={{padding:"10px 12px 10px 16px",borderBottom:`1px solid ${C.borderSoft}`,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
        <span style={{fontSize:15,fontWeight:600,color:C.text}}>{ordering?t("Порядок проектов"):t("Колоды на сегодня")}</span>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {allDecks.length>1&&<UiButton size="sm" variant={ordering?"primary":"secondary"} icon={ordering?"check":"move"}
            onClick={()=>ordering?setOrdering(false):startOrdering()} title={t("Порядок, в котором берутся новые карточки")}>
            {ordering?t("Готово"):t("Порядок")}</UiButton>}
        </div>
      </div>
      {ordering&&(
        <div>
          <div style={{padding:"10px 16px",fontSize:13,color:C.mid,lineHeight:1.45,borderBottom:`1px solid ${C.borderSoft}`}}>
            {t("Новые карточки берутся сверху вниз. Перетащите проект за ручку слева.")}
          </div>
          {orderList.map((p,i)=>(
            <div key={p.id} ref={el=>{rowRefs.current[p.id]=el;}}
              style={{display:"flex",alignItems:"center",gap:8,minHeight:48,padding:"2px 8px 2px 6px",borderBottom:`1px solid ${C.borderSoft}`,
                background:draggingId===p.id?C.bluePale:"transparent"}}>
              <span onPointerDown={e=>onGripDown(e,p.id)} onPointerMove={onGripMove} onPointerUp={onGripUp} onPointerCancel={onGripUp}
                title={t("Перетащить")} style={{touchAction:"none",cursor:draggingId===p.id?"grabbing":"grab",color:C.faint,padding:"10px",display:"inline-flex",borderRadius:8}}>
                <Icon n="grip" s={16}/></span>
              <span style={{fontSize:12.5,color:C.faint,width:20,textAlign:"right",fontVariantNumeric:"tabular-nums"}}>{i+1}</span>
              <span style={{flex:1,minWidth:0,fontSize:14,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name}</span>
              <button className="ac-ibtn" onClick={()=>nudge(p.id,-1)} disabled={i===0} title={t("Выше")} aria-label={t("Выше")} style={{background:"none",border:"none",color:i===0?C.border:C.mid,cursor:i===0?"default":"pointer",width:40,height:40,alignItems:"center",justifyContent:"center",display:"inline-flex",borderRadius:8}}><Icon n="chevronUp" s={16}/></button>
              <button className="ac-ibtn" onClick={()=>nudge(p.id,1)} disabled={i===orderList.length-1} title={t("Ниже")} aria-label={t("Ниже")} style={{background:"none",border:"none",color:i===orderList.length-1?C.border:C.mid,cursor:i===orderList.length-1?"default":"pointer",width:40,height:40,alignItems:"center",justifyContent:"center",display:"inline-flex",borderRadius:8}}><Icon n="chevronDown" s={16}/></button>
            </div>
          ))}
        </div>
      )}
      {!ordering&&<>
      {/* Column labels */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 72px 56px",gap:2,padding:"8px 12px 6px 16px",borderBottom:`1px solid ${C.borderSoft}`,alignItems:"center",fontSize:12.5,color:C.faint}}>
        <div>{t("Проект — нажмите, чтобы повторить только его")}</div>
        <div style={{textAlign:"center",color:C.green}}>{t("К повторению")}</div>
        <div style={{textAlign:"center",color:C.blueDark}}>{t("Новые")}</div>
      </div>
      {/* Deck rows */}
      {sortByDeckOrder(deckData.map(d=>d.p)).map(p=>deckData.find(d=>d.p.id===p.id)).map(({p,newC,learnC,reviewC,total,allCards,waiting})=>{
        const barMaxPct=Math.max(14,(allCards/maxCards)*100);
        const donePct=allCards>0?((allCards-total)/allCards)*100:0;
        return(
        <div key={p.id} role="button" tabIndex={0} onClick={()=>go(p.id)} onKeyDown={e=>{if(e.key==="Enter"||e.key===" "){e.preventDefault();go(p.id);}}}
          title={t("Повторить «{0}»",[p.name])} className="ac-row-click"
          style={{"--ac-row-hover":C.surface,display:"grid",gridTemplateColumns:"1fr 72px 56px",gap:2,minHeight:52,padding:"4px 12px 4px 16px",borderBottom:`1px solid ${C.borderSoft}`,cursor:"pointer",alignItems:"center"}}>
          <div style={{overflow:"hidden"}}>
            <div style={{fontSize:14,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name}</div>
            {waiting>0&&<div style={{fontSize:12,color:C.faint,marginTop:2}}>{t("ещё {0} новых — в следующие дни",[waiting])}</div>}
            <div style={{marginTop:5,width:`${barMaxPct}%`,height:4,borderRadius:2,background:C.surface,overflow:"hidden"}} title={t("{0} из {1} карточек пройдено",[allCards-total,allCards])}>
              <div style={{height:"100%",borderRadius:2,background:C.blue,width:`${donePct}%`,transition:"width .3s"}}/>
            </div>
          </div>
          <div style={{display:"flex",justifyContent:"center",alignItems:"center"}}>
            {learnC.length+reviewC.length>0&&<span style={{fontSize:13.5,fontWeight:600,color:C.green,fontVariantNumeric:"tabular-nums"}}>{learnC.length+reviewC.length}</span>}
          </div>
          <div style={{display:"flex",justifyContent:"center",alignItems:"center"}}>
            {newC.length>0&&<span style={{fontSize:13.5,fontWeight:600,color:C.blueDark,fontVariantNumeric:"tabular-nums"}}>{newC.length}</span>}
          </div>
        </div>
        );
      })}
      <div style={{padding:"10px 16px",fontSize:13,color:C.mid}}>{t("Всего на сегодня:")} {nCards(totalDue)}</div>
      </>}
      <Heatmap theme={theme} forecast={forecast} lang={lang}/>
    </div>
  );

  return <div style={{width:"100%",flexShrink:0}}>{inner}</div>;
}

// Стрик по всем языкам: текущий, рекорд и занимались ли сегодня.
export function readStreak(){
  const a=getStudyDays();const s=calcStats(a);
  return{count:s.currentStreak,best:s.longestStreak,studiedToday:(a[localDateKey()]||0)>0};
}
